import { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Col, Row } from 'react-bootstrap'
import { Helmet } from 'react-helmet'

import { DismissableAppPromoBanner } from 'components/banners/app-promo-banner'
import { getIsBabylistAppInstalled, getIsMobile } from 'store/reducers'
import { tracking, track, useTracking } from 'lib/analytics'
import { ProductCollection } from 'shared/product-collections'
import useOnCurrentUserLoaded from 'shared/hooks/useOnCurrentUserLoaded/useOnCurrentUserLoaded'

import StoreHero from './hero'
import ContentContainer from '../content-container'
import CategoryGrid from '../CategoryGrid'
import WhyShopBabylist from './why-shop-babylist'
import { CreateBabylistCTA, FindBabylistCTA } from './call-to-action-box'
import css from './landing-page.scss'
import Disclaimers from './disclaimers'
import { SubHeading } from '../../../components/headings'
import CmsLandingPage from './CmsLandingPage/CmsLandingPage'
import { useCuratedProductCollections } from '../../api/hooks'
import { CANONICAL_URL } from 'shared/constants'

@tracking()
class BaseLandingPage extends Component {
  render() {
    const { showAppPromoBanner, productCollections, categories } = this.props
    return (
      <div>
        {showAppPromoBanner && <DismissableAppPromoBanner />}

        <Helmet
          meta={[
            {
              name: 'description',
              content:
                'Browse the Babylist shop to discover the best baby gear tested by experts and real parents. Get anything you need for the baby in your life or start a registry.',
            },
          ]}
          title="Baby Clothes, Gear & More - Babylist Shop"
          link={[{ rel: 'canonical', href: `${CANONICAL_URL}/store` }]}
        />
        <StoreHero />
        <div className="container">
          <ContentContainer>
            {productCollections &&
              productCollections.map(
                ({ id: collectionId, title, teaser, products }) => (
                  <section
                    className={css.collectionContainer}
                    key={collectionId}
                  >
                    <ProductCollection
                      key={collectionId}
                      id={collectionId}
                      title={title}
                      teaser={teaser}
                      products={products.map(
                        ({
                          brand,
                          id,
                          image: { url: imageUrl },
                          name,
                          path: productUrl,
                          price,
                          rating,
                          reviewCount,
                        }) => ({
                          brand,
                          id,
                          imageUrl,
                          name,
                          price,
                          productUrl,
                          rating,
                          reviewCount,
                        })
                      )}
                      maxVisibleProducts={null}
                    />
                  </section>
                )
              )}

            <section>
              <SubHeading
                style={{ fontSize: '24px' }}
                className="mtn mbm text-bold text-center h4 pbm bbs"
              >
                Browse by Category
              </SubHeading>
              <CategoryGrid categories={categories} />
            </section>

            <WhyShopBabylist />
            <Row className="mvxl">
              <Col sm={6}>
                <CreateBabylistCTA />
              </Col>
              <Col sm={6}>
                <FindBabylistCTA />
              </Col>
            </Row>
            <Disclaimers />
          </ContentContainer>
        </div>
      </div>
    )
  }
}

BaseLandingPage.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          urlSlug: PropTypes.string.isRequired,
          image: PropTypes.string,
        })
      ),
      urlSlug: PropTypes.string.isRequired,
      image: PropTypes.string,
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  productCollections: PropTypes.array,
  showAppPromoBanner: PropTypes.bool,
}

BaseLandingPage.defaultProps = {
  productCollections: [],
  showAppPromoBanner: false,
}

const _LandingPage = (props) => {
  const { cmsLandingPageLayout } = props
  const tracker = useTracking()

  useOnCurrentUserLoaded((currentUser) => {
    const registryProps = {
      isRegistryOwner: !!currentUser?.registryId,
      registryOwnerId: currentUser?.id,
      registryId: currentUser?.registryId,
    }
    tracker.trackEvent({ event: track.storeLandingViewed, ...registryProps })
  })

  const { data, isLoading } = useCuratedProductCollections()
  const curatedProductCollections = isLoading ? null : data?.productCollections

  return (
    <>
      {cmsLandingPageLayout ? (
        <CmsLandingPage {...props} />
      ) : (
        <BaseLandingPage
          {...props}
          productCollections={curatedProductCollections}
        />
      )}
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    categories: state.categoryTree,
    installedBabylistApp: getIsBabylistAppInstalled(state),
    isMobile: getIsMobile(state),
  }
}

const ConnectedLandingPage = connect(mapStateToProps)(_LandingPage)

// eslint-disable-next-line import/no-default-export
export default ConnectedLandingPage
export const LandingPage = ConnectedLandingPage
